import { Box, MenuItem, SxProps, TextField, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlPermissions, GqlProtocolVisit } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import { ProtocolVisitCondition, ProtocolVisitConditionType } from "../../types/ProtocolVisitCondition";
import VisitConditionTypeSwitch from "./VisitConditionTypeSwitch";

type Props = {
    allVisits: GqlProtocolVisit[];
    condition: ProtocolVisitCondition;
    onChange: (condition?: ProtocolVisitCondition) => void;
    sx?: SxProps<Theme>;
};

const conditionTypes = [
    { value: "and", label: "protocolVisitConditionsEditor.conditionAnd" },
    { value: "or", label: "protocolVisitConditionsEditor.conditionOr" },
    {
        value: "minDate",
        label: "protocolVisitConditionsEditor.conditionMinDate",
    },
    {
        value: "maxDate",
        label: "protocolVisitConditionsEditor.conditionMaxDate",
    },
    {
        value: "beforeVisit",
        label: "protocolVisitConditionsEditor.conditionBeforeVisit",
    },
    {
        value: "afterVisit",
        label: "protocolVisitConditionsEditor.conditionAfterVisit",
    },
];

export default function ProtocolVisitConditionsEditor({ allVisits, condition, onChange, sx = undefined }: Props) {
    const { t } = useTranslation("protocol");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 1,
                ...sx,
            }}
        >
            <Box
                sx={
                    !condition.type || condition.type === "and" || condition.type === "or"
                        ? { width: "100%" }
                        : { flex: 1, minWidth: "128px", maxWidth: "200px" }
                }
            >
                <TextField
                    disabled={!isAdmin}
                    select
                    fullWidth
                    label={t("protocolVisitConditionsEditor.selectConditionType")}
                    value={condition.type || "and"}
                    onChange={e =>
                        onChange({
                            type: e.target.value as ProtocolVisitConditionType,
                            id: condition.id,
                        })
                    }
                >
                    {conditionTypes.map(conditionType => (
                        <MenuItem key={conditionType.value} value={conditionType.value}>
                            {t(conditionType.label)}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <Box sx={{ flex: 1, minWidth: "128px" }}>
                <VisitConditionTypeSwitch allVisits={allVisits} condition={condition} onChange={onChange} />
            </Box>
        </Box>
    );
}
